import React, { useRef, useEffect, useMemo } from 'react';
import { useStore } from '@tanstack/react-form';
import { Box, Typography } from '@mui/material';
import type {
  FieldSection,
  DetailFormApi,
  RichTextOptions,
  DetailFormBeforeFieldRenderer,
  DetailFormCustomFieldRenderer,
} from './types';
import type { ColumnMetadata, LookupDataItem } from '../grid';
import { parseValidationRules, validateField } from '../grid/utils';
import { DetailFormField } from './DetailFormField';
import { useGetLookupQuery } from '../grid';
import { resolveReactNode } from './reactNodeRegistry';
import {
  getFieldOccurrences,
  resolveFieldOccurrenceMetadata,
  type FieldOccurrenceResolver,
} from './resolveFieldOccurrences';
import {
  getFieldGridColumnSx,
  getResponsiveGridColumnSx,
  resolveFieldColumnSpan,
} from './responsiveWidthUtils';
import { shouldFieldSpanFullWidth } from './detailFormFieldUtils';

type DetailFormSectionProps = {
  section: FieldSection;
  fields: ColumnMetadata[];
  form: DetailFormApi;
  lookupMaps?: Record<string, Record<string, string>>;
  lookupOptionMaps?: Record<string, Record<string, string>>;
  lookupDataArrays?: Record<string, LookupDataItem[]>;
  disabled?: boolean;
  mode: 'add' | 'edit';
  richTextOptions?: RichTextOptions;
  /**
   * The content key (nodeKey) of the detail form's grid. Required to fetch
   * dependent lookups reactively via GetLookup when a field has dependsOn.
   */
  contentKey?: string;
  /** The ID of the parent record (entity being edited). Used to render ReactNode fields. */
  parentId?: string | number;
  /** The data source (table name) of the entity being edited. Used to resolve entityTypeId for ReactNode fields. */
  dataSource?: string;
  /**
   * Resolves duplicate-dataField occurrences (e.g. two zReactNode fields sharing
   * a generic placeholder dataField like "nuPickers.Picker") to the correct
   * ColumnMetadata entry by position in the layout. Falls back to a plain
   * dataField lookup when not provided.
   */
  resolveFieldOccurrence?: FieldOccurrenceResolver;
  customFieldRenderer?: DetailFormCustomFieldRenderer;
  renderBeforeField?: DetailFormBeforeFieldRenderer;
};

// ---------------------------------------------------------------------------
// DependentLookupField
// ---------------------------------------------------------------------------
// Renders a selectgrid field whose lookup options are filtered by the current
// value of another form field (declared in fieldMetadata.dependsOn).
// Re-fetches from the API whenever that dependent value changes, and clears
// the field's own value so no stale selection is kept.
// ---------------------------------------------------------------------------

type DependentLookupFieldProps = {
  fieldName: string;
  fieldMetadata: ColumnMetadata;
  form: DetailFormApi;
  contentKey: string;
  disabled: boolean;
  isRequired: boolean;
  richTextOptions?: RichTextOptions;
  lookupMap?: Record<string, string>;
  lookupOptionMap?: Record<string, string>;
  lookupDataArray?: LookupDataItem[];
  mode: 'add' | 'edit';
  hideCaption?: boolean;
  captionsWidth?: FieldSection['captionsWidth'];
};

const DependentLookupField = ({
  fieldName,
  fieldMetadata,
  form,
  contentKey,
  disabled,
  isRequired,
  richTextOptions,
  lookupMap,
  lookupOptionMap,
  lookupDataArray,
  mode,
  hideCaption,
  captionsWidth,
}: DependentLookupFieldProps) => {
  const dep = fieldMetadata.dependsOn![0];
  // dep.dataSource    -> the OTHER field's dataField (the parent dropdown), e.g. "TtProject"
  // dep.dependentField -> the column name on each lookup row that holds the parent id, e.g. "ProjectId"

  // Subscribe to the parent field's current value in the form

  const parentValue = useStore(form.store, (s: any) => s.values?.[dep.dataSource]);
  const parentId =
    parentValue !== undefined && parentValue !== null && String(parentValue) !== ''
      ? String(parentValue)
      : undefined;

  // Clear this field when the parent value changes
  const prevParentRef = useRef(parentId);
  useEffect(() => {
    if (prevParentRef.current !== parentId) {
      prevParentRef.current = parentId;
      form.setFieldValue(fieldName, '');
    }
  }, [parentId, fieldName, form]);

  // Re-fetch the lookup whenever the parent changes by passing parent value as
  // `selectedEntity` (mirrors legacy detail.js GetDropDownStore(opts, col, selectedId)).
  // RTK Query's cache key includes selectedEntity, so each new parent value triggers
  // a fresh request. We still apply a client-side filter below as a safety net for
  // backends that return all rows regardless of selectedEntity.
  const { data: lookupResult, isLoading: lookupLoading } = useGetLookupQuery(
    {
      nodeKey: contentKey,
      fieldKey: fieldMetadata.key!,
      displayFieldName: fieldMetadata.additionalData?.displayExpr,
      defaultFilters: fieldMetadata.defaultFilters,
      objectId: 0,
      selectedEntity: parentId,
    },
    { skip: !fieldMetadata.key || !parentId }
  );

  // Filter the lookup array to rows where row[dep.dependentField] === parentId.
  // This is a defensive client-side filter: if the backend ALREADY scoped the
  // results via `selectedEntity`, the filter is a no-op. We only apply it when
  // we can actually verify membership via Fields metadata — otherwise we pass
  // the result through as-is so the dropdown stays editable.
  const filtered = useMemo(() => {
    if (!lookupResult) return undefined;
    if (!parentId) return { map: {}, array: [] as LookupDataItem[] };

    // No items have Fields (backend returned Key/Value only) → trust the server
    const hasAnyFields = lookupResult.array.some((item) => Array.isArray(item.Fields));
    if (!hasAnyFields) return lookupResult;

    // No item has the dependentField column at all → trust the server (we can't verify)
    const hasDependentColumn = lookupResult.array.some((item) =>
      item.Fields?.some((f) => f.Name === dep.dependentField)
    );
    if (!hasDependentColumn) return lookupResult;

    const matches = (item: LookupDataItem) => {
      const fieldEntry = item.Fields?.find((f) => f.Name === dep.dependentField);
      // Items missing the dependent column pass through (defensive)
      if (!fieldEntry) return true;
      return String(fieldEntry.StringValue) === parentId;
    };

    const array = lookupResult.array.filter(matches);
    const allArray = (lookupResult.allArray ?? lookupResult.array).filter(matches);
    const allowedIds = new Set(array.map((it) => String(it.Id)));
    const allAllowedIds = new Set(allArray.map((it) => String(it.Id)));
    const map = Object.entries(lookupResult.map).reduce<Record<string, string>>(
      (acc, [id, label]) => {
        if (allowedIds.has(id)) acc[id] = label;
        return acc;
      },
      {}
    );
    const allMap = Object.entries(lookupResult.allMap ?? lookupResult.map).reduce<
      Record<string, string>
    >((acc, [id, label]) => {
      if (allAllowedIds.has(id)) acc[id] = label;
      return acc;
    }, {});

    return { map, array, allMap, allArray };
  }, [lookupResult, parentId, dep.dependentField]);

  const effectiveLookupMap = parentId ? (filtered?.allMap ?? filtered?.map ?? {}) : lookupMap;
  const effectiveLookupDataArray = parentId ? (filtered?.array ?? []) : lookupDataArray;
  const hasEffectiveLookupOptions = !!(
    effectiveLookupDataArray?.length || Object.keys(effectiveLookupMap ?? {}).length
  );

  const rules = parseValidationRules(fieldMetadata.validationRules);
  const label = fieldMetadata.caption || fieldMetadata.title || fieldName;

  return (
    <form.Field
      name={fieldName}
      validators={{
        onSubmit: ({ value }) => validateField(rules, value, label),
        onChange: ({ value }) => validateField(rules, value, label),
      }}
    >
      {(field) => (
        <DetailFormField
          fieldName={fieldName}
          metadata={fieldMetadata}
          field={field}
          lookupMap={effectiveLookupMap}
          lookupOptionMap={parentId ? (filtered?.map ?? {}) : lookupOptionMap}
          lookupDataArray={effectiveLookupDataArray}
          isLoading={!!parentId && lookupLoading && !hasEffectiveLookupOptions}
          disabled={
            disabled ||
            fieldMetadata.allowEditing === false ||
            !parentId ||
            (mode === 'add' && fieldMetadata.canCreate === false) ||
            (mode === 'edit' && fieldMetadata.canEdit === false)
          }
          isRequired={isRequired}
          richTextOptions={richTextOptions}
          hideCaption={hideCaption}
          captionsWidth={captionsWidth}
        />
      )}
    </form.Field>
  );
};

export const DetailFormSection = ({
  section,
  fields,
  form,
  lookupMaps,
  lookupOptionMaps,
  lookupDataArrays,
  disabled = false,
  mode,
  richTextOptions,
  contentKey,
  parentId,
  dataSource,
  resolveFieldOccurrence,
  customFieldRenderer,
  renderBeforeField,
}: DetailFormSectionProps) => {
  const formValues = useStore(form.store, (state: any) => state.values) as Record<string, unknown>;

  /** BO field width 1–11 only: legacy detail sections otherwise stack fields vertically. */
  const usesInnerFieldGrid = React.useMemo(
    () =>
      getFieldOccurrences(section).some((occurrence, indexInSection) => {
        const fieldMetadata =
          resolveFieldOccurrence?.(section.key, indexInSection) ??
          resolveFieldOccurrenceMetadata(occurrence, fields);
        if (!fieldMetadata) return false;
        const span = resolveFieldColumnSpan(fieldMetadata);
        return span != null && span < 12;
      }),
    [section, fields, resolveFieldOccurrence]
  );

  const resolvedFields = React.useMemo(() => {
    return getFieldOccurrences(section)
      .map((occurrence, indexInSection) => {
        const fieldMetadata =
          resolveFieldOccurrence?.(section.key, indexInSection) ??
          resolveFieldOccurrenceMetadata(occurrence, fields);

        if (!fieldMetadata) {
          return null;
        }

        if (fieldMetadata.canView === false) return null;

        return {
          fieldName: occurrence.fieldName ?? null,
          fieldMetadata,
          indexInSection,
        };
      })
      .filter(
        (
          item
        ): item is {
          fieldName: string | null;
          fieldMetadata: ColumnMetadata;
          indexInSection: number;
        } => item !== null
      );
  }, [section, fields, resolveFieldOccurrence]);

  return (
    <Box sx={{ mb: 0, maxWidth: '100%', ...getResponsiveGridColumnSx(section.width) }}>
      {!section.hideCaption && section.title && (
        <Typography variant="subtitle2" sx={{ mb: 1.5, mx: 1, mt: 0.5, fontWeight: 700 }}>
          {section.title}
        </Typography>
      )}
      <Box
        sx={{
          ...(usesInnerFieldGrid
            ? {
                display: 'grid',
                gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
              }
            : {
                display: 'flex',
                flexDirection: 'column',
              }),
          gap: 0.5,
          width: '100%',
          px: section.title ? 1 : 0,
          pb: 0.75,
        }}
      >
        {resolvedFields.map(({ fieldName, fieldMetadata, indexInSection }) => {
          const rules = parseValidationRules(fieldMetadata.validationRules);
          const required = rules.some((r) => r.type === 'required');
          const label = fieldMetadata.caption || fieldMetadata.title || fieldName || '';
          const fieldDisabled =
            disabled ||
            fieldMetadata.allowEditing === false ||
            (mode === 'add' && fieldMetadata.canCreate === false) ||
            (mode === 'edit' && fieldMetadata.canEdit === false);
          const beforeField = fieldName
            ? renderBeforeField?.({ fieldName, metadata: fieldMetadata })
            : undefined;
          const wrapField = (content: React.ReactNode) => (
            <Box
              key={`${section.key}:${indexInSection}`}
              sx={
                usesInnerFieldGrid
                  ? getFieldGridColumnSx(fieldMetadata, {
                      forceFullWidth: shouldFieldSpanFullWidth(fieldMetadata),
                    })
                  : { width: '100%' }
              }
            >
              {beforeField}
              {content}
            </Box>
          );

          if (fieldMetadata.dataType === 'zReactNode') {
            const node = resolveReactNode(fieldMetadata.renderingClass, {
              entityId: parentId !== undefined ? Number(parentId) : undefined,
              canCreate: fieldMetadata.canCreate,
              canEdit: fieldMetadata.canEdit,
              dataSource,
              value: fieldName ? formValues[fieldName] : undefined,
            });
            if (!node) return null;
            return wrapField(node);
          }

          if (!fieldName) return null;

          // Fields with dependsOn use reactive per-field fetching
          if (fieldMetadata.dependsOn?.length && contentKey && fieldMetadata.key) {
            return wrapField(
              <DependentLookupField
                fieldName={fieldName}
                fieldMetadata={fieldMetadata}
                form={form}
                contentKey={contentKey}
                disabled={disabled}
                mode={mode}
                isRequired={required}
                richTextOptions={richTextOptions}
                lookupMap={lookupMaps?.[fieldName]}
                lookupOptionMap={lookupOptionMaps?.[fieldName]}
                lookupDataArray={lookupDataArrays?.[fieldName]}
                hideCaption={section.hideCaption}
                captionsWidth={section.captionsWidth}
              />
            );
          }

          return wrapField(
            <form.Field
              name={fieldName}
              validators={{
                onSubmit: ({ value }) => validateField(rules, value, label),
                onChange: ({ value }) => validateField(rules, value, label),
              }}
            >
              {(field) =>
                customFieldRenderer?.({
                  fieldName,
                  metadata: fieldMetadata,
                  field,
                  disabled: fieldDisabled,
                  isRequired: required,
                  hideCaption: section.hideCaption,
                  captionsWidth: section.captionsWidth,
                }) ?? (
                  <DetailFormField
                    fieldName={fieldName}
                    metadata={fieldMetadata}
                    field={field}
                    lookupMap={lookupMaps?.[fieldName]}
                    lookupOptionMap={lookupOptionMaps?.[fieldName]}
                    lookupDataArray={lookupDataArrays?.[fieldName]}
                    disabled={fieldDisabled}
                    isRequired={required}
                    richTextOptions={richTextOptions}
                    hideCaption={section.hideCaption}
                    captionsWidth={section.captionsWidth}
                  />
                )
              }
            </form.Field>
          );
        })}
      </Box>
    </Box>
  );
};
